import { useState, useEffect } from 'react'
import { FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import DocumentViewerModal from './DocumentViewerModal'
import { api } from '../../lib/api'
import { documentPdfUrl } from '../../lib/documentPdfUrl'

export interface DocumentAcceptanceData {
  document_type: string
  document_version: string
}

interface DocumentInfo {
  document_type: string
  name: string
  version: string
  filename: string
  updated_at: string
}

interface DocumentAcceptanceProps {
  onChange: (acceptances: DocumentAcceptanceData[], allAccepted: boolean) => void
  disabled?: boolean
  variant?: 'light' | 'dark'
}

export default function DocumentAcceptance({
  onChange,
  disabled = false,
  variant = 'light',
}: DocumentAcceptanceProps) {
  const [documents, setDocuments] = useState<DocumentInfo[]>([])
  const [acceptedDocs, setAcceptedDocs] = useState<Set<string>>(new Set())
  const [viewerOpen, setViewerOpen] = useState(false)
  const [selectedDoc, setSelectedDoc] = useState<DocumentInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const { t, i18n } = useTranslation(['auth', 'common'])

  useEffect(() => {
    fetchDocuments()
  }, [])

  const fetchDocuments = async () => {
    try {
      const response = await api.get('/documents/list')
      setDocuments(response.data)
    } catch (error) {
      console.error('Błąd podczas pobierania listy dokumentów:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCheckboxChange = (docType: string, version: string, checked: boolean) => {
    const newAccepted = new Set(acceptedDocs)
    
    if (checked) {
      newAccepted.add(docType)
    } else {
      newAccepted.delete(docType)
    }
    
    setAcceptedDocs(newAccepted)

    // Build acceptances array
    const acceptances: DocumentAcceptanceData[] = documents
      .filter(doc => newAccepted.has(doc.document_type))
      .map(doc => ({
        document_type: doc.document_type,
        document_version: doc.version
      }))

    const allAccepted = newAccepted.size === documents.length
    onChange(acceptances, allAccepted)
  }

  const handleViewDocument = (doc: DocumentInfo) => {
    setSelectedDoc(doc)
    setViewerOpen(true)
  }

  const isDark = variant === 'dark'

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#FF5A00]"></div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
        {t('auth:register.documents.heading')}
      </p>

      {documents.map((doc) => {
        const isAccepted = acceptedDocs.has(doc.document_type)

        return (
          <div key={doc.document_type} className="flex items-start space-x-3">
            <input
              type="checkbox"
              id={`doc-${doc.document_type}`}
              checked={isAccepted}
              onChange={(e) => handleCheckboxChange(doc.document_type, doc.version, e.target.checked)}
              disabled={disabled}
              className={`mt-1 h-4 w-4 rounded ${
                isDark
                  ? 'text-[#FF5A00] focus:ring-[#FF5A00] border-gray-500 bg-[#1a1a1a]'
                  : 'text-[#212121] focus:ring-[#212121] border-gray-300'
              }`}
            />
            <div className="flex-1">
              <label
                htmlFor={`doc-${doc.document_type}`}
                className={`text-sm cursor-pointer ${isDark ? 'text-gray-400' : 'text-gray-700'}`}
              >
                {t('auth:register.documents.accept')}{' '}
                <button
                  type="button"
                  onClick={() => handleViewDocument(doc)}
                  className={`font-medium inline-flex items-center gap-1 hover:underline ${
                    isDark ? 'text-white hover:text-gray-200' : 'text-gray-700 hover:text-[#212121]'
                  }`}
                  disabled={disabled}
                >
                  <FileText className="h-3.5 w-3.5" />
                  {doc.name}
                </button>
                {' '}{t('auth:register.documents.version', { version: doc.version })}
              </label>
            </div>
          </div>
        )
      })}

      {documents.length > 0 && acceptedDocs.size < documents.length && (
        <p className={`text-xs ${isDark ? 'text-red-400' : 'text-red-600'}`}>
          * {t('common:form.documentsRequired')}
        </p>
      )}

      {selectedDoc && (
        <DocumentViewerModal
          isOpen={viewerOpen}
          onClose={() => setViewerOpen(false)}
          documentUrl={documentPdfUrl(selectedDoc.document_type, i18n.language)}
          documentName={selectedDoc.name}
        />
      )}
    </div>
  )
}
